import { useMemo, useEffect, useRef, useCallback } from 'react';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';

/**
 * Detecta se a string é HTML bruto (em vez de Markdown).
 * Reconhece <!DOCTYPE, <html, ou tags HTML estruturais comuns no início.
 */
export function isHtmlContent(str) {
    if (!str) return false;
    const t = str.trimStart();
    if (/^<!doctype\s/i.test(t)) return true;
    if (/^<html\b/i.test(t)) return true;
    return /<(head|body|table|div|span|img|ul|ol|h[1-6])\b/i.test(str.slice(0, 1000));
}

/**
 * Bloco <style> de override injetado no HTML do comunicado para torná-lo
 * responsivo ao container do popup/preview (remove dimensões fixas como
 * width:1280px; height:720px de flyers/thumbnails).
 */
const HTML_OVERRIDE = `<style id="__pb_override">
html,body{
  width:100%!important;max-width:100%!important;
  height:auto!important;min-height:unset!important;
  overflow:visible!important;overflow-x:hidden!important;
}
*{box-sizing:border-box!important;max-width:100%!important;}
table{width:100%!important;}
img{height:auto!important;}
</style>`;

function injectOverride(html) {
    if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${HTML_OVERRIDE}</head>`);
    if (/<head>/i.test(html)) return html.replace(/<head>/i, `<head>${HTML_OVERRIDE}`);
    return `<html><head>${HTML_OVERRIDE}</head><body>${html}</body></html>`;
}

/**
 * BroadcastHtmlContent — renderiza HTML bruto isolado em um <iframe srcdoc>.
 *
 * O iframe oferece isolamento total de CSS/JS sem vazamento para a página mãe.
 * Após o load, mede `body.scrollHeight` e ajusta a altura do iframe ao
 * conteúdo, deixando o scroll a cargo do container externo. Um ResizeObserver
 * mantém a altura sincronizada quando o conteúdo muda (ex: fontes carregam).
 */
export function BroadcastHtmlContent({ html }) {
    const iframeRef = useRef(null);
    const srcDoc = useMemo(() => injectOverride(html), [html]);

    const fitHeight = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const body = iframe.contentDocument?.body;
            if (body) iframe.style.height = `${body.scrollHeight}px`;
        } catch {
            // cross-origin — não deve ocorrer com srcdoc
        }
    }, []);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const onLoad = () => {
            fitHeight();
            try {
                const ro = new ResizeObserver(fitHeight);
                ro.observe(iframe.contentDocument.body);
                iframe._pbRo = ro;
            } catch { /* ignore */ }
        };

        iframe.addEventListener('load', onLoad);
        return () => {
            iframe.removeEventListener('load', onLoad);
            iframe._pbRo?.disconnect();
        };
    }, [fitHeight, srcDoc]);

    return (
        <iframe
            ref={iframeRef}
            srcDoc={srcDoc}
            sandbox="allow-same-origin"
            title="Comunicado HTML"
            className="w-full border-0 block rounded-lg"
            style={{ minHeight: 200 }}
        />
    );
}

/**
 * BroadcastContent — renderiza o conteúdo do comunicado.
 *
 * Decide entre iframe (HTML bruto isolado) e MarkdownRenderer com base em:
 * 1. `format` explícito ('html' | 'markdown') quando fornecido (preferencial);
 * 2. detecção automática `isHtmlContent` como fallback para conteúdo legado.
 *
 * @param {{ content: string, format?: 'html'|'markdown' }} props
 */
export default function BroadcastContent({ content, format }) {
    const useHtml = format ? format === 'html' : isHtmlContent(content);
    return useHtml
        ? <BroadcastHtmlContent html={content} />
        : <MarkdownRenderer content={content} />;
}
